import React, { useState } from 'react';
import { RestaurantInfo } from './types';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Plus, Utensils } from 'lucide-react';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { RestaurantForm } from './RestaurantForm';
import { format } from 'date-fns';
import { PageSelectionModal } from './PageSelectionModal';
import { normalizeCoordinates } from '@/lib/coordinates-utils';

interface RestaurantManagerProps {
  pageId: string;
  restaurants: RestaurantInfo[];
  updateRestaurants: (restaurants: RestaurantInfo[]) => void;
  collapsedItems: Record<string, boolean>;
  onToggleItem: (id: string) => void;
  hideAddButton?: boolean;
  hideTitle?: boolean;
  allPages?: any[];
  removeRestaurantFromPage?: (pageId: string, restaurantId: string) => void;
  addRestaurantToPage?: (pageId: string, restaurant: RestaurantInfo) => void;
  globalMoveRestaurantToPage?: (sourcePageId: string, restaurantId: string, targetPageId: string) => void;
}

export function RestaurantManager({ 
  pageId, 
  restaurants, 
  updateRestaurants,
  collapsedItems,
  onToggleItem,
  hideAddButton = false,
  hideTitle = false,
  allPages = [],
  removeRestaurantFromPage,
  addRestaurantToPage,
  globalMoveRestaurantToPage
}: RestaurantManagerProps) {
  // Adicionar log para debug
  console.log('RestaurantManager - allPages:', allPages);
  
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isProcessingRestaurantDescription, setIsProcessingRestaurantDescription] = useState(false);
  const [isProcessingRestaurantNameToDescription, setIsProcessingRestaurantNameToDescription] = useState<Record<string, boolean>>({});
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);
  const [restaurantToMove, setRestaurantToMove] = useState<string>('');

  // Escutar eventos de expansão de itens para colapsar os restaurantes quando outro item for expandido
  React.useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // Se o evento for de outra página ou for para um restaurante, ignorar
      if (eventPageId !== pageId || itemType === 'restaurant') {
        return;
      }
      
      // Just notify parent component instead of trying to auto-collapse specific items
      onToggleItem('');
    };
    
    // Adicionar o event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remover ao desmontar o componente
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [pageId, onToggleItem]);

  // Função para adicionar novo restaurante
  const addRestaurant = () => {
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const newRestaurant: RestaurantInfo = {
      id: `restaurant-${Date.now()}`,
      name: '', // Nome vazio para que o componente RestaurantForm aplique o fallback
      date: formattedDate,
      time: '',
      duration: '',
      location: '',
      description: '',
      price: undefined,
      currency: 'BRL',
      images: [],
      globalOrder: restaurants.length + 1,  // Increment by 1 to place at the end
      cuisineType: '',
      menuHighlights: '',
      city: '',
      country: ''
    };
    
    logAndUpdateRestaurants([...(restaurants || []), newRestaurant]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(newRestaurant.id);
    }, 0);
  };

  // Função para remover um restaurante
  const removeRestaurant = (pageId: string, restaurantId: string) => {
    const updatedRestaurants = restaurants.filter(restaurant => restaurant.id !== restaurantId);
    logAndUpdateRestaurants(updatedRestaurants);
  };

  // Função para atualizar um campo específico de um restaurante
  const updateRestaurant = (pageId: string, restaurantId: string, field: keyof RestaurantInfo, value: any) => {
    let safeValue;
    
    // Special handling for coordinates to ensure they're always properly formatted numbers
    if (field === 'coordinates' && value) {
      // Use our normalize function for consistent cross-platform handling
      safeValue = normalizeCoordinates(value);
      
      if (!safeValue) {
        console.error("[RestaurantManager] Failed to normalize coordinates:", value);
        // Use a fallback to prevent null coordinates
        safeValue = { lat: 0, lng: 0 };
      }
      
      console.log("[RestaurantManager] Normalized coordinates:", {
        original: value,
        normalized: safeValue,
        types: {
          originalLatType: typeof value.lat,
          originalLngType: typeof value.lng,
          normalizedLatType: typeof safeValue.lat,
          normalizedLngType: typeof safeValue.lng
        }
      });
    }
    else {
      // For all other fields, use regular deep copy
      safeValue = typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;
    }
    
    const updatedRestaurants = restaurants.map((restaurant: RestaurantInfo) => {
      if (restaurant.id === restaurantId) {
        const updated = { ...restaurant, [field]: safeValue };
        
        // Extra debugging for coordinate updates
        if (field === 'coordinates') {
          console.log('[RestaurantManager] Updated restaurant with new coordinates:', {
            restaurant: restaurant.id,
            name: restaurant.name,
            oldCoordinates: restaurant.coordinates,
            newCoordinates: safeValue,
            fullObject: updated
          });
        }
        
        return updated;
      }
      return restaurant;
    });
    
    console.log(`[RestaurantManager] Updated field ${String(field)}:`, {
      restaurantId,
      fieldName: field,
      newValue: safeValue,
      valueType: typeof safeValue,
      isObject: typeof safeValue === 'object',
      hasCoordinates: field === 'coordinates'
    });
    
    logAndUpdateRestaurants(updatedRestaurants);
  };

  // Função para atualizar múltiplos campos de um restaurante de uma vez
  const batchUpdateRestaurant = (restaurantId: string, updates: Partial<RestaurantInfo>) => {
    console.log('[RestaurantManager] Batch updating restaurant:', {
      restaurantId,
      updates
    });
    
    // Create a copy of updates for safe modification
    const safeUpdates = { ...updates };
    
    // Ensure coordinates are normalized if present
    if (updates.coordinates) {
      const normalizedCoords = normalizeCoordinates(updates.coordinates);
      
      if (normalizedCoords) {
        safeUpdates.coordinates = normalizedCoords;
      } else {
        console.error('[RestaurantManager] Failed to normalize coordinates in batch update:', updates.coordinates);
        // Remove invalid coordinates to prevent saving errors
        delete safeUpdates.coordinates;
      }
    }
    
    const updatedRestaurants = restaurants.map((restaurant: RestaurantInfo) => {
      if (restaurant.id === restaurantId) {
        // Create a deep copy of the restaurant with updates applied
        const updated = {
          ...restaurant,
          ...safeUpdates
        };
        
        // For debugging coordinate updates
        if (updates.coordinates) {
          console.log('[RestaurantManager] Updated restaurant with new coordinates in batch:', {
            restaurantId,
            name: restaurant.name,
            oldCoordinates: restaurant.coordinates,
            newCoordinates: safeUpdates.coordinates,
            fullObject: updated
          });
        }
        
        return updated;
      }
      return restaurant;
    });
    
    logAndUpdateRestaurants(updatedRestaurants);
  };

  // Função para alternar o estado de colapso de um restaurante
  const toggleRestaurantCollapse = (restaurantId: string) => {
    // If empty ID, it's just a signal to collapse all - forward to parent component
    if (!restaurantId) {
      onToggleItem('');
      return;
    }
    
    // Se o item atual está colapsado (fechado), vamos abri-lo e fechar todos os outros
    if (collapsedItems[restaurantId]) {
      // Abrir apenas o item atual
      onToggleItem(restaurantId);
      
      // Disparar um evento para notificar outros componentes que um restaurante foi expandido
      document.dispatchEvent(new CustomEvent('item-expanded', {
        detail: {
          itemType: 'restaurant',
          itemId: restaurantId,
          pageId: pageId
        }
      }));
    } else {
      // Se está aberto, apenas fechamos
      onToggleItem(restaurantId);
    }
  };

  // Função para duplicar um restaurante
  const duplicateRestaurant = (sourcePageId: string, restaurantId: string) => {
    const restaurantToDuplicate = restaurants.find(
      (restaurant: RestaurantInfo) => restaurant.id === restaurantId
    );
    
    if (!restaurantToDuplicate) return;
    
    // Deep clone the restaurant object
    const duplicatedRestaurant: RestaurantInfo = JSON.parse(JSON.stringify(restaurantToDuplicate));
    
    // Update properties for the duplicate
    duplicatedRestaurant.id = `restaurant-${Date.now()}`;
    
    // Se o nome original não for vazio, adicione "(Cópia)" ao nome
    // Se o nome original for vazio, mantenha vazio para que o placeholder seja exibido
    duplicatedRestaurant.name = duplicatedRestaurant.name 
      ? `${duplicatedRestaurant.name} (Cópia)` 
      : '';
      
    duplicatedRestaurant.globalOrder = restaurants.length + 1; // Place at the end
    
    // Add the duplicated restaurant to the list
    logAndUpdateRestaurants([...restaurants, duplicatedRestaurant]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      onToggleItem(duplicatedRestaurant.id);
    }, 0);
    
    toast({
      title: "Restaurante duplicado",
      description: "O restaurante foi duplicado com sucesso.",
    });
  };
  
  // Função para mover um restaurante para outra página
  const moveRestaurantToPage = (sourcePageId: string, restaurantId: string) => {
    // Debug logs
    console.log('moveRestaurantToPage INTERNAL - called with:', {
      sourcePageId, 
      restaurantId
    });
    console.log('moveRestaurantToPage INTERNAL - allPages:', allPages);
    console.log('moveRestaurantToPage INTERNAL - allPages length:', allPages?.length);
    console.log('moveRestaurantToPage INTERNAL - filtered pages:', allPages?.filter(p => p.id !== sourcePageId && !p.excludeFromDropdown));
    
    // Check if we have multiple pages to move to (excluding current page and pages with excludeFromDropdown=true)
    const availablePages = allPages?.filter(p => p.id !== sourcePageId && !p.excludeFromDropdown) || [];
    
    if (availablePages.length === 0) {
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover o restaurante.",
      });
      return;
    }

    setRestaurantToMove(restaurantId);
    setIsPageSelectionOpen(true);
  };

  // Function to handle the page selection
  const handleMoveToPage = (targetPageId: string) => {
    console.log('handleMoveToPage - restaurant to move:', restaurantToMove);
    
    if (!restaurantToMove) return;
    
    // Find the restaurant to move
    const restaurant = restaurants.find(r => r.id === restaurantToMove);
    console.log('handleMoveToPage - restaurant found:', restaurant);
    
    if (!restaurant) return;
    
    // Close the modal
    setIsPageSelectionOpen(false);
    
    // If we have the global move function, use it directly
    if (globalMoveRestaurantToPage) {
      console.log('handleMoveToPage - using globalMoveRestaurantToPage with:', {
        sourcePageId: pageId, 
        restaurantId: restaurantToMove, 
        targetPageId
      });
      
      globalMoveRestaurantToPage(pageId, restaurantToMove, targetPageId);
      
      toast({
        title: "Restaurante movido",
        description: `O restaurante foi movido para a página selecionada.`,
      });
      
      // Clear the restaurant to move
      setRestaurantToMove('');
      return;
    }
    
    // If we have the helper functions, use them to properly move the restaurant
    if (removeRestaurantFromPage && addRestaurantToPage) {
      // Make a deep copy of the restaurant to avoid reference issues
      const restaurantCopy = JSON.parse(JSON.stringify(restaurant));
      
      // Add to target page first, then remove from current page
      addRestaurantToPage(targetPageId, restaurantCopy);
      
      // Short delay before removing from current page to ensure UI updates properly
      setTimeout(() => {
        removeRestaurantFromPage(pageId, restaurantToMove);
        
        toast({
          title: "Restaurante movido",
          description: `O restaurante foi movido para a página selecionada.`,
        });
        
        // Clear the restaurant to move
        setRestaurantToMove('');
      }, 50);
    } else {
      // Fallback: just show a toast that we can't move the restaurant
      toast({
        title: "Funcionalidade em desenvolvimento",
        description: "A função de mover para outra página será implementada em breve.",
      });
      
      // Clear the restaurant to move
      setRestaurantToMove('');
    }
  };

  // Function adapters to match RestaurantForm expected props
  const updateRestaurantAdapter = (pageId: string, restaurantId: string, field: keyof RestaurantInfo, value: any) => {
    updateRestaurant(pageId, restaurantId, field, value);
  };

  const logAndUpdateRestaurants = (updatedRestaurants: RestaurantInfo[]) => {
    console.log('Restaurantes antes de salvar:', updatedRestaurants.map(r => ({
      id: r.id, 
      city: r.city, 
      country: r.country
    })));
    updateRestaurants(updatedRestaurants);
  };

  return (
    <div className="space-y-4">
      {/* Page Selection Modal */}
      {isPageSelectionOpen && restaurantToMove && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => {
            setIsPageSelectionOpen(false);
            setRestaurantToMove('');
          }}
          pages={allPages || []}
          currentPageId={pageId}
          onSelectPage={handleMoveToPage}
          title="Mover restaurante para outra página"
        />
      )}

      {!hideTitle && (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium">Restaurantes</h3>
          {!hideAddButton && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
              onClick={addRestaurant}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Restaurante
            </Button>
          )}
        </div>
      )}

      {hideTitle && !hideAddButton && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-white text-[#087a83] border-[#087a83] hover:bg-[#087a83] hover:text-white"
            onClick={addRestaurant}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Restaurante
          </Button>
        </div>
      )}

      {(!restaurants || restaurants.length === 0) ? (
        <div className="p-6 border border-dashed rounded-md text-center">
          <Utensils className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">Você ainda não adicionou nenhum restaurante a esta cotação.</p>
          {!hideAddButton && (
            <Button 
              variant="outline"
              onClick={addRestaurant}
              className="bg-white hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Restaurante
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {restaurants.map((restaurant: RestaurantInfo, idx: number) => (
            <RestaurantForm
              key={restaurant.id}
              restaurant={restaurant}
              index={idx}
              pageId={pageId}
              updateRestaurant={updateRestaurantAdapter}
              batchUpdateRestaurant={batchUpdateRestaurant}
              deleteRestaurant={removeRestaurant}
              duplicateRestaurant={duplicateRestaurant}
              moveRestaurantToPage={(restaurantId) => moveRestaurantToPage(pageId, restaurantId)}
              isCollapsed={collapsedItems[restaurant.id] !== false}
              onToggleCollapse={() => toggleRestaurantCollapse(restaurant.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
} 